import React from 'react';
import { useWatch } from 'react-hook-form';
import {
  ACTION_PRIORITY_OPTIONS,
  visibleActionStatusOptions,
  type ActionAssigneeKind,
} from '../ICSConstants';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  ACTION_CLASSIFICATION_HINT,
  ACTION_CLASSIFICATION_OPTIONS,
} from '../actionClassification';
import type { ActionEntity } from '../hooks/useScopedActionForm';
import { ActionTicketLayout } from './ActionTicketLayout';
import { ActionDetailsSidebar } from './ActionDetailsSidebar';
import { ResourceRequirementsSection } from './ResourceRequirementsSection';
import { HazardsSection } from './HazardsSection';
import {
  ActionAttachmentsSection,
  type AttachableForm,
} from './ActionAttachmentsSection';
import {
  actionPriorityBadgeVariant,
  actionStatusBadgeVariant,
} from '../actionsTable/actionEditModel';
import { TicketSelectField } from './TicketSelectField';
import { TicketTextAreaField } from './TicketTextAreaField';
import type {
  ActionAttachment,
  ActionHazard,
  ActionResourceRequirement,
  DailyProgressReport,
  TaskComment,
} from './actionTicketModel';
import { TaskCommentsSection } from './TaskCommentsSection';
import { DailyProgressReportsSection } from './DailyProgressReportsSection';

type Option = { label: string; value: string };

export type ActionTicketBodyProps = {
  entity?: ActionEntity;
  displayId: string | null;
  archived?: boolean;
  onArchiveClick?: () => void;

  assigneeKind: ActionAssigneeKind;
  assigneeKindOptions: Option[];
  pocOptions: Option[];
  teamOptions: Option[];
  positionOptions: Option[];
  roleOptions?: Option[];
  aorOptions: Option[];
  aorTitle?: string;
  aorMulti?: boolean;
  objectiveOptions?: Option[];
  /** Incident scope only; org actions are not tied to ICS-202 objectives. */
  showObjective?: boolean;
  workspaceLabel: string;
  dependencyChoices: Option[];
  dependsOn: string[];
  onDependsOnChange: (next: string[]) => void;
  responsibleIndividualOptions?: Option[];
  responsibleIndividualIds?: string[];
  onResponsibleIndividualIdsChange?: (next: string[]) => void;
  assigneeId?: string | null;
  /** Incident scope only; org actions reach neither the ICS-233 nor the ICS-215. */
  showClassification?: boolean;

  requirements: ActionResourceRequirement[];
  onRequirementsChange: (next: ActionResourceRequirement[]) => void;
  requirementsNa?: boolean;
  onRequirementsNaChange?: (next: boolean) => void;
  hazards: ActionHazard[];
  onHazardsChange: (next: ActionHazard[]) => void;
  attachments: ActionAttachment[];
  onAttachmentsChange: (next: ActionAttachment[]) => void;
  formOptions: AttachableForm[];
  /** Scopes the file picker; absent for an org action. */
  incidentId?: string | null;
  /** Roster seat to return to from a linked 213RR. */
  positionName?: string;

  /** HWCG discussion thread; each post is a new action version. */
  comments?: TaskComment[];
  onCommentPost?: (next: TaskComment[]) => void | Promise<void>;
  commentsReadOnly?: boolean;
  currentUserGuid?: string;
  currentUserName?: string;
  /** HWCG daily progress reports; rendered above the Prerequisite Checklist. */
  dailyProgressReports?: DailyProgressReport[];
  onDailyProgressReportsChange?: (
    next: DailyProgressReport[]
  ) => void | Promise<void>;
  /**
   * HWCG: Current/Ongoing locked until GS + BD Approve Execution (3
   * signatures).
   */
  disableCurrentOngoing?: boolean;

  /**
   * Fields the ticket shows but does not own: location, checklist, symbology.
   * They are passed in rather than imported so this component stays cheap to
   * render on its own, in a story or anywhere else.
   */
  extraFields?: React.ReactNode;
  /**
   * Prerequisite Checklist (HWCG) / Sub-Tasks. HWCG renders it above resource
   * requests.
   */
  subTasks?: React.ReactNode;
  /** Files derived from position-tagged folders; empty for most tasks. */
  positionFiles?: React.ReactNode;
};

/**
 * The ticket itself: identity header, a main column of the work, and the
 * DETAILS rail. Rendered by `ActionDrawer` when `ACTION_TICKETS` is on, and
 * expects to sit inside that drawer's `FormProvider`.
 */
export const ActionTicketBody: React.FC<ActionTicketBodyProps> = ({
  entity,
  displayId,
  archived,
  onArchiveClick,
  assigneeKind,
  assigneeKindOptions,
  pocOptions,
  teamOptions,
  positionOptions,
  roleOptions,
  aorOptions,
  aorTitle,
  aorMulti,
  objectiveOptions,
  showObjective = false,
  workspaceLabel,
  dependencyChoices,
  dependsOn,
  onDependsOnChange,
  responsibleIndividualOptions,
  responsibleIndividualIds,
  onResponsibleIndividualIdsChange,
  assigneeId,
  showClassification = false,
  requirements,
  onRequirementsChange,
  requirementsNa,
  onRequirementsNaChange,
  hazards,
  onHazardsChange,
  attachments,
  onAttachmentsChange,
  formOptions,
  incidentId,
  positionName,
  comments = [],
  onCommentPost,
  commentsReadOnly = false,
  dailyProgressReports = [],
  onDailyProgressReportsChange,
  currentUserGuid = '',
  currentUserName,
  disableCurrentOngoing = false,
  extraFields,
  subTasks,
  positionFiles,
}) => {
  const hwcg = useHwcgFlag();
  const statusOptions = visibleActionStatusOptions(hwcg);
  const watchedStatus = useWatch({ name: 'status' }) as string | undefined;
  return (
    <ActionTicketLayout
      entity={entity}
      displayId={displayId}
      archived={archived}
      onArchiveClick={onArchiveClick}
      details={
        <ActionDetailsSidebar
          assigneeKind={assigneeKind}
          assigneeKindOptions={assigneeKindOptions}
          pocOptions={pocOptions}
          teamOptions={teamOptions}
          positionOptions={positionOptions}
          roleOptions={roleOptions}
          aorOptions={aorOptions}
          aorTitle={aorTitle}
          aorMulti={aorMulti}
          objectiveOptions={objectiveOptions}
          showObjective={showObjective}
          workspaceLabel={workspaceLabel}
          dependencyChoices={dependencyChoices}
          dependsOn={dependsOn}
          onDependsOnChange={onDependsOnChange}
          responsibleIndividualOptions={responsibleIndividualOptions}
          responsibleIndividualIds={responsibleIndividualIds}
          onResponsibleIndividualIdsChange={onResponsibleIndividualIdsChange}
          assigneeId={assigneeId}
        />
      }
    >
      <div className="tw:space-y-4">
        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
          <TicketSelectField
            title="Status"
            fieldName="status"
            options={statusOptions}
            rules={{ required: { value: true, message: 'Status is required' } }}
            optionBadgeVariant={actionStatusBadgeVariant}
            disabledValues={
              disableCurrentOngoing ? ['Current/Ongoing'] : undefined
            }
          />
          <TicketSelectField
            title="Priority"
            fieldName="priority"
            options={ACTION_PRIORITY_OPTIONS}
            optionBadgeVariant={actionPriorityBadgeVariant}
          />
        </div>
        {showClassification ? (
          <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
            <TicketSelectField
              title="Classification"
              fieldName="classification"
              options={ACTION_CLASSIFICATION_OPTIONS}
            />
            <p className="tw:self-end tw:text-[0.675rem] tw:text-muted-foreground">
              {ACTION_CLASSIFICATION_HINT}
            </p>
          </div>
        ) : null}
        <TicketTextAreaField
          title="Description"
          fieldName="description"
          placeholder="Optional detail: what done looks like, constraints, references."
          rules={{
            maxLength: {
              value: 2000,
              message: 'This maximum length is 2000 characters',
            },
          }}
        />
        {hwcg && onDailyProgressReportsChange ? (
          <DailyProgressReportsSection
            value={dailyProgressReports}
            onChange={onDailyProgressReportsChange}
            status={watchedStatus}
            readOnly={commentsReadOnly}
            incidentId={incidentId}
            currentUserGuid={currentUserGuid}
            currentUserName={currentUserName}
          />
        ) : null}
        {hwcg ? subTasks : null}
        <ResourceRequirementsSection
          value={requirements}
          onChange={onRequirementsChange}
          notApplicable={requirementsNa}
          onNotApplicableChange={onRequirementsNaChange}
          positionName={positionName}
        />
        <HazardsSection value={hazards} onChange={onHazardsChange} />
        <ActionAttachmentsSection
          value={attachments}
          onChange={onAttachmentsChange}
          formOptions={formOptions}
          incidentId={incidentId}
        />
        {hwcg && onCommentPost ? (
          <TaskCommentsSection
            value={comments}
            onPost={onCommentPost}
            readOnly={commentsReadOnly}
            currentUserGuid={currentUserGuid}
            currentUserName={currentUserName}
          />
        ) : null}
        {positionFiles}
        {extraFields}
      </div>
    </ActionTicketLayout>
  );
};

export default ActionTicketBody;
